import type { QueryParams } from '@/lib/api/client';

/**
 * Centralized query-key factory (plan §4.4). Every key is [domain, scope, ...params] so
 * mutations can invalidate precise families. Lists embed their params for cache separation.
 */
export const keys = {
  me: () => ['me'] as const,
  dashboard: () => ['dashboard'] as const,
  users: {
    all: () => ['users'] as const,
    list: (p?: QueryParams) => ['users', 'list', p ?? {}] as const,
  },
  apiTokens: {
    all: () => ['api-tokens'] as const,
    list: (p?: QueryParams) => ['api-tokens', 'list', p ?? {}] as const,
    options: () => ['api-tokens', 'options'] as const,
  },
  blocks: { schemas: () => ['blocks', 'schemas'] as const },
  collectionSchemas: () => ['collection-schemas'] as const,
  pages: {
    all: () => ['pages'] as const,
    list: (p?: QueryParams) => ['pages', 'list', p ?? {}] as const,
    detail: (id: string) => ['pages', 'detail', id] as const,
    sections: (id: string) => ['pages', 'sections', id] as const,
  },
  families: {
    all: () => ['families'] as const,
    list: (p?: QueryParams) => ['families', 'list', p ?? {}] as const,
    detail: (id: string) => ['families', 'detail', id] as const,
  },
  content: {
    all: (collection: string) => ['content', collection] as const,
    list: (collection: string, p?: QueryParams) => ['content', collection, 'list', p ?? {}] as const,
    detail: (collection: string, id: string) => ['content', collection, 'detail', id] as const,
  },
  media: {
    all: () => ['media'] as const,
    list: (p?: QueryParams) => ['media', 'list', p ?? {}] as const,
    detail: (id: string) => ['media', 'detail', id] as const,
    usages: (id: string) => ['media', 'usages', id] as const,
  },
  leads: {
    all: (type: string) => ['leads', type] as const,
    lists: (type: string) => ['leads', type, 'list'] as const,
    list: (type: string, p?: QueryParams) => ['leads', type, 'list', p ?? {}] as const,
    detail: (type: string, id: string) => ['leads', type, 'detail', id] as const,
  },
  /**
   * Student records. `all(id)` is a prefix of every per-record key, so saving the
   * profile can refresh the tier panel and the history feed in one invalidation
   * without naming eight caches — and `lists()` is separate so an erasure can
   * refresh the list WITHOUT refetching the record that no longer exists.
   */
  students: {
    root: () => ['students'] as const,
    lists: () => ['students', 'list'] as const,
    list: (p?: QueryParams) => ['students', 'list', p ?? {}] as const,
    /* The student schema, custom-field registry composed in. Not per-record: it
       is the SHAPE every record shares, and the list, the form and the export
       all read the same cached copy (PHASE 12). */
    schema: () => ['students', 'schema'] as const,
    /* The custom-field registry itself, edited from Settings. Separate from the
       schema above because it also carries the ARCHIVED rows, which the schema
       deliberately does not. */
    customFields: (includeArchived: boolean) =>
      ['students', 'custom-fields', includeArchived] as const,
    customField: (id: string) => ['students', 'custom-fields', 'detail', id] as const,
    all: (id: string) => ['students', 'record', id] as const,
    detail: (id: string) => ['students', 'record', id, 'detail'] as const,
    summary: (id: string) => ['students', 'record', id, 'summary'] as const,
    applications: (id: string) => ['students', 'record', id, 'applications'] as const,
    /* Bring a friend (PHASE 05): the box and the seminar credits, one read per record. */
    referrals: (id: string) => ['students', 'record', id, 'referrals'] as const,
    /* An enrolment's stage history, by enrolment id (round 10). */
    stages: (aid: string) => ['students', 'enrolment', aid, 'stages'] as const,
    /* The parent release signed for an enrolment, as stored (round 13). */
    release: (aid: string) => ['students', 'enrolment', aid, 'release'] as const,
    projects: (id: string) => ['students', 'record', id, 'projects'] as const,
    certificates: (id: string) => ['students', 'record', id, 'certificates'] as const,
    surveys: (id: string) => ['students', 'record', id, 'surveys'] as const,
    surveyInvitations: (id: string) => ['students', 'record', id, 'survey-invitations'] as const,
    /* Not per-record: the published instruments are a small shared register, and
       the picker on one student's Surveys tab warms it for the next. */
    surveyInstruments: () => ['students', 'survey-instruments'] as const,
    /* One stored instrument by id, which is how a RESPONSE resolves its prompts:
       the version it was answered against may be older than the published one or
       archived outright, and neither is in the register above. */
    surveyInstrument: (iid: string) => ['students', 'survey-instrument', iid] as const,
    emails: (id: string, p?: QueryParams) =>
      ['students', 'record', id, 'emails', p ?? {}] as const,
    history: (id: string, p?: QueryParams) =>
      ['students', 'record', id, 'history', p ?? {}] as const,
    /* The alumni panel: every asked enrolment and the consented record, if any. */
    alumni: (id: string) => ['students', 'record', id, 'alumni'] as const,
    /* The dated notes (round 10). Under `all(id)`, so an erasure clears them. */
    notes: (id: string) => ['students', 'record', id, 'notes'] as const,
    /* Round 10, phase 22. All under `all(id)`, so an erasure or a merge clears them. */
    consents: (id: string) => ['students', 'record', id, 'consents'] as const,
    timeline: (id: string) => ['students', 'record', id, 'timeline'] as const,
    possibleMatches: (id: string) => ['students', 'record', id, 'possible-matches'] as const,
    merges: (id: string) => ['students', 'record', id, 'merges'] as const,
    emailLog: (id: string, p?: QueryParams) =>
      ['students', 'record', id, 'email-log', p ?? {}] as const,
    compare: (id: string, other: string) => ['students', 'compare', id, other] as const,
    /* By enrolment id, like `stages`. */
    payments: (aid: string) => ['students', 'enrolment', aid, 'payments'] as const,
    cohortGroups: (cohortId: string) => ['students', 'cohort-groups', cohortId] as const,
    /* The system-wide queue on /duplicates. */
    duplicates: (p?: QueryParams) => ['students', 'duplicates', p ?? {}] as const,
  },
  /**
   * Consented alumni records (PHASE 13). Their own family, not a students one:
   * a record outlives the student it came from once retention erases them, so
   * it cannot hang off `students.all(id)`. `all()` prefixes the list keys, so a
   * delete from either the list or a student's panel refreshes every page.
   */
  alumni: {
    all: () => ['alumni-records'] as const,
    lists: () => ['alumni-records', 'list'] as const,
    list: (p?: QueryParams) => ['alumni-records', 'list', p ?? {}] as const,
  },
  /* The instrument library, edited from Settings. Separate from `students`
     because it is not a dependent record of any one student — the same reason
     be-svet mounts it on its own registrar. */
  surveyInstruments: {
    all: () => ['survey-instruments'] as const,
    list: (status?: string) => ['survey-instruments', 'list', status ?? 'all'] as const,
  },
  /* The CSV import (round 10, PHASE 21). One import's view holds its rows; the
     targets are what its columns can map to (fixed + this cohort's). */
  imports: {
    list: (cohortId?: string) => ['imports', 'list', cohortId ?? ''] as const,
    detail: (id: string) => ['imports', 'detail', id] as const,
    targets: (id: string) => ['imports', 'targets', id] as const,
    presets: () => ['imports', 'presets'] as const,
  },
  cohorts: {
    all: () => ['cohorts'] as const,
    list: (p?: QueryParams) => ['cohorts', 'list', p ?? {}] as const,
    detail: (id: string) => ['cohorts', 'detail', id] as const,
    roster: (id: string) => ['cohorts', 'roster', id] as const,
    sessions: (id: string) => ['cohorts', 'sessions', id] as const,
    /* The alumni ask plan: who would be emailed, recomputed each time the dialog opens. */
    alumniPlan: (id: string) => ['cohorts', 'alumni-plan', id] as const,
    // Round 10, phase 23: the set-up tabs.
    schema: () => ['cohorts', 'schema'] as const,
    groups: (id: string) => ['cohorts', 'groups', id] as const,
    files: (id: string) => ['cohorts', 'files', id] as const,
    emailTemplates: (id: string) => ['cohorts', 'email-templates', id] as const,
    waitlist: (id: string) => ['cohorts', 'waitlist', id] as const,
    scholarshipDecisions: (id: string) => ['cohorts', 'scholarship-decisions', id] as const,
    certificateBatch: (batch: string) => ['cohorts', 'certificate-batch', batch] as const,
    certificateReadiness: (id: string) => ['cohorts', 'certificate-readiness', id] as const,
    // Round 10, phase 24: the work screens.
    dashboard: (id: string) => ['cohorts', 'dashboard', id] as const,
    groupStatus: (id: string, gid: string) => ['cohorts', 'group-status', id, gid] as const,
    surveyResults: (ids: readonly string[]) => ['cohorts', 'survey-results', [...ids].sort()] as const,
    // Bring a friend (round 16, PHASE 04): the cohort tab's referrer table and Needs a match.
    referrals: (id: string) => ['cohorts', 'referrals', id] as const,
  },
  referrals: {
    suggestions: (id: string) => ['referrals', 'suggestions', id] as const,
  },
  /* Round 10, phase 24. Across every cohort, so neither hangs off `cohorts`. */
  needsAction: {
    all: () => ['needs-action'] as const,
    list: (p?: QueryParams) => ['needs-action', 'list', p ?? {}] as const,
  },
  numbers: () => ['admin-numbers'] as const,
  /* Round 10, phase 25: the console-wide email log, the interest leads and the
     Calendly bookings. A send invalidates `emailLog.all()`. */
  emailLog: {
    all: () => ['email-log'] as const,
    list: (p?: QueryParams) => ['email-log', 'list', p ?? {}] as const,
  },
  interestLeads: {
    all: () => ['interest-leads'] as const,
    list: (p?: QueryParams) => ['interest-leads', 'list', p ?? {}] as const,
  },
  /* B-R16-07: the sidebar's unopened counts, one request for all five lists. */
  leadsUnread: () => ['leads-unread'] as const,
  calendlyBookings: {
    all: () => ['calendly-bookings'] as const,
    list: (p?: QueryParams) => ['calendly-bookings', 'list', p ?? {}] as const,
  },
  /**
   * Bulk email. `all()` is a prefix of both the list and every detail key, so a
   * send can refresh the list and the campaign it just queued in one call — and
   * `preview(id)` is its own key because it is the ONLY thing the Send control
   * reads: an invalidation of the campaign after an edit must drop the stale
   * preview with it, or the screen would offer to send what nobody has seen.
   */
  campaigns: {
    all: () => ['campaigns'] as const,
    lists: () => ['campaigns', 'list'] as const,
    list: (p?: QueryParams) => ['campaigns', 'list', p ?? {}] as const,
    detail: (id: string) => ['campaigns', 'detail', id] as const,
    preview: (id: string) => ['campaigns', 'preview', id] as const,
    recipients: (id: string, p?: QueryParams) =>
      ['campaigns', 'detail', id, 'recipients', p ?? {}] as const,
  },
  /* Transactional templates, edited from Settings. The list carries the
     available keys in its meta, so there is one key for both. */
  emailTemplates: {
    all: () => ['email-templates'] as const,
    list: () => ['email-templates', 'list'] as const,
    preview: (key: string) => ['email-templates', 'preview', key] as const,
  },
  revisions: (entityType: string, entityId: string) =>
    ['revisions', entityType, entityId] as const,
  audit: (p?: QueryParams) => ['audit', p ?? {}] as const,
  oauth: {
    request: (id: string) => ['oauth', 'request', id] as const,
    connectedApps: {
      all: () => ['oauth', 'connected-apps'] as const,
      list: (p?: QueryParams) => ['oauth', 'connected-apps', 'list', p ?? {}] as const,
    },
    connectInfo: () => ['oauth', 'connect-info'] as const,
    grants: {
      all: () => ['oauth', 'grants'] as const,
      list: (p?: QueryParams) => ['oauth', 'grants', 'list', p ?? {}] as const,
    },
    clients: {
      all: () => ['oauth', 'clients'] as const,
      list: (p?: QueryParams) => ['oauth', 'clients', 'list', p ?? {}] as const,
    },
  },
  /* Bring a friend's launch state (PHASE 05): the gate, the launch, the cohorts' pending counts. */
  bringAFriend: {
    state: () => ['bring-a-friend', 'state'] as const,
  },
  settings: {
    doc: () => ['settings', 'doc'] as const,
    capabilities: () => ['settings', 'capabilities'] as const,
    strings: () => ['settings', 'strings'] as const,
    navigation: (key: string) => ['settings', 'navigation', key] as const,
    redirects: (p?: QueryParams) => ['settings', 'redirects', p ?? {}] as const,
    contactDirectory: () => ['settings', 'contact-directory'] as const,
    countries: () => ['settings', 'countries'] as const,
    countryGroups: () => ['settings', 'country-groups'] as const,
    artwork: () => ['settings', 'artwork'] as const,
    /* The retention dry run and whether erasure is confirmed. */
    retention: () => ['settings', 'retention'] as const,
    /* Round 10, phase 26. `['settings', 'survey']` is the key the cohort
       Surveys tab already reads the same document under. */
    interviewers: () => ['settings', 'interviewers'] as const,
    legacyCohorts: () => ['settings', 'legacy-cohorts'] as const,
    legacyCohort: (id: string) => ['settings', 'legacy-cohorts', id] as const,
    publicNumbers: () => ['settings', 'public-numbers'] as const,
    goals: () => ['settings', 'goals'] as const,
    survey: () => ['settings', 'survey'] as const,
  },
  /** The public-numbers check (`/numbers/public`): the four headline numbers, current vs calculated. */
  publicNumbers: () => ['public-numbers'] as const,
  /** The teacher's own groups (`/teach`), scoped on the server to the signed-in teacher. */
  teach: {
    all: () => ['teach'] as const,
    groups: () => ['teach', 'groups'] as const,
    group: (gid: string) => ['teach', 'group', gid] as const,
  },
  /**
   * Page copy — a Content surface (`/content/pages`), not a Settings one, since PHASE 04
   * moved it there. The keys moved with it so a cache invalidation reads as the screen it
   * refreshes rather than as the tab it used to live in.
   */
  pageCopy: {
    list: () => ['page-copy'] as const,
    detail: (ns: string) => ['page-copy', ns] as const,
    schema: (ns: string) => ['page-copy', ns, 'schema'] as const,
    usages: (ns: string) => ['page-copy', ns, 'usages'] as const,
  },
  search: (q: string, locale: string) => ['search', q, locale] as const,
} as const;
